import React, { useRef, forwardRef, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { SocialChat, MenuClose } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { animated, useSpring } from 'react-spring'
import { boolAttr } from '@fs/zion-frontend-friends'
import { useChatState } from '../../state/ChatState'
import { useChatOverlayContext } from './ChatOverlayProvider'
import useIsMobile from '../../hooks/useIsMobile'

const chatButtonCss = css`
  border: none;
  box-sizing: border-box;
  height: 56px;
  padding: 0 16px;
  background: white;
  border-radius: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  bottom: 24px;
  right: 24px;
  transition: 0.2s;
  cursor: pointer;
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12), 0px 3px 5px rgba(0, 0, 0, 0.2);
  z-index: 2;
  font-weight: 500;
  font-size: 20px;

  &:hover {
    background: #ddd;
  }

  &[is-mobile] {
    bottom: 16px;
    right: 16px;
  }
`

const connectLabelCss = css`
  overflow: hidden;
  & > div {
    width: max-content;
  }
`

const unreadIndicatorCss = css`
  height: 24px;
  width: 24px;
  position: absolute;
  top: -6px;
  right: -6px;
  border-radius: 12px;
  background: #dd3b3b;
  color: white;
  font-size: 12px;
  text-align: center;
  line-height: 24px;
  font-weight: 700;
`

const warningIndicatorCss = css`
  height: 24px;
  width: 24px;
  position: absolute;
  top: -6px;
  right: -6px;
  border-radius: 12px;
  background: orange;
  color: white;
  font-size: 12px;
  text-align: center;
  line-height: 24px;
  font-weight: 700;
`

function ConnectButton(props, ref) {
  const [t] = useTranslation()
  const labelRef = useRef()
  const initialRenderRef = useRef(true)
  const { unreadMessages, connection, failedToConnect } = useChatState()
  const { isOpen, toggleChatOverlay } = useChatOverlayContext()
  const shouldAnimate = isOpen && !!connection
  const [labelWidth, setLabelWidth] = useState()
  const isMobile = useIsMobile()

  useEffect(() => {
    // This allows the styles to start correctly set - probably a better way to do this
    if (!initialRenderRef.current && connection) {
      setLabelWidth(labelRef.current?.scrollWidth)
    } else {
      initialRenderRef.current = false
    }
  }, [connection, isOpen, shouldAnimate])

  const buttonAnimStyles = useSpring({
    padding: shouldAnimate || isMobile ? '0 16px' : '0 20px',
    color: shouldAnimate ? 'var(--background-primary)' : '#d83b95',
    config: {
      clamp: true,
      tension: 500,
      mass: 0.75,
    },
  })

  const labelAnimStyles = useSpring({
    width: shouldAnimate ? 0 : labelWidth,
    marginLeft: shouldAnimate ? 0 : 8,
    config: {
      clamp: true,
      tension: 500,
      mass: 0.75,
    },
  })

  return (
    <animated.button
      ref={ref}
      css={chatButtonCss}
      type="button"
      data-testid="connect-button"
      onClick={toggleChatOverlay}
      {...props}
      style={buttonAnimStyles}
      is-mobile={boolAttr(isMobile)}
    >
      {shouldAnimate ? <MenuClose /> : <SocialChat />}

      {!isMobile && (
        <animated.div style={labelAnimStyles} css={connectLabelCss}>
          <div ref={labelRef}>{t('chat.connect', 'Connect')}</div>
        </animated.div>
      )}
      {unreadMessages?.length > 0 && <div css={unreadIndicatorCss}>{unreadMessages.length}</div>}
      {failedToConnect && <div css={warningIndicatorCss}>!</div>}
    </animated.button>
  )
}

export default forwardRef(ConnectButton)
